import { produce, Draft } from 'immer'
import { IAction, TActionTypes } from '@Root/types'

import {
  ExternalChannelActionTypes,
  TExternalChannelActionTypes,
  IExternalChannelState,
  IGetExternalVideosSuccessResponseBody,
  IGetExternalChannelsSuccessResponseBody
} from './types'

export const initialState: IExternalChannelState = {
  externalChannels: [],
  externalVideos: [],
  loadingVideos: false,
  loadingChannels: false,
  hasLoadedVideosOnce: false,
  page: {
    totalCount: 0,
    totalPages: 1,
    currentPage: 1,
    videosPerPage: 10,
    actionsDropdownOpen: false,
  }
}

type TAction = TActionTypes | TExternalChannelActionTypes

export const externalChannelsReducer = (
  state: IExternalChannelState = initialState,
  action: TAction,
): IExternalChannelState => {
  return produce(state, draft => {
    switch (action.type) {
      case ExternalChannelActionTypes.GET_EXTERNAL_VIDEOS:
        getExternalVideos(draft)
        break

      case ExternalChannelActionTypes.GET_EXTERNAL_VIDEOS_FAILURE:
        getExternalVideosFailure(draft)
        break

      case ExternalChannelActionTypes.GET_EXTERNAL_VIDEOS_SUCCESS:
        getExternalVideosSuccess(draft, action)
        break

      case ExternalChannelActionTypes.INCREASE_PAGE_COUNT:
        increasePageCount(draft)
        break

      case ExternalChannelActionTypes.DECREASE_PAGE_COUNT:
        decreasePageCount(draft)
        break

      case ExternalChannelActionTypes.GET_EXTERNAL_CHANNELS:
        getExternalChannels(draft)
        break

      case ExternalChannelActionTypes.GET_EXTERNAL_CHANNELS_FAILURE:
        getExternalChannelsFailure(draft)
        break

      case ExternalChannelActionTypes.GET_EXTERNAL_CHANNELS_SUCCESS:
        getExternalChannelsSuccess(draft, action)
        break

      case ExternalChannelActionTypes.OPEN_ACTIONS_DROPDOWN:
        openActionsDropdown(draft)
        break

      case ExternalChannelActionTypes.CLOSE_DROPDOWNS:
        closeDropdown(draft)
        break

      default:
        break
    }
  })
}

const getExternalVideos = (draft: Draft<IExternalChannelState>) => {
  draft.loadingVideos = true
}

const getExternalVideosFailure = (draft: Draft<IExternalChannelState>) => {
  draft.loadingVideos = false
}

function isIGetExternalVideosSuccessAction(arg: any): arg is IAction<IGetExternalVideosSuccessResponseBody> {
  return arg.type === ExternalChannelActionTypes.GET_EXTERNAL_VIDEOS_SUCCESS
}

const getExternalVideosSuccess = (draft: Draft<IExternalChannelState>, action: TAction) => {
  if (!isIGetExternalVideosSuccessAction(action)) { return }

  const newExternalVideos = action.payload.videos
  const newTotalCount = newExternalVideos.length

  draft.externalVideos = newExternalVideos
  draft.page.totalCount = newTotalCount
  draft.page.totalPages = Math.floor(newTotalCount / draft.page.videosPerPage) + 1
  draft.loadingVideos = false
  draft.hasLoadedVideosOnce = true
}

const increasePageCount = (draft: Draft<IExternalChannelState>) => {
  draft.page.currentPage = draft.page.currentPage + 1
}

const decreasePageCount = (draft: Draft<IExternalChannelState>) => {
  draft.page.currentPage = draft.page.currentPage - 1
}

const getExternalChannels = (draft: Draft<IExternalChannelState>) => {
  draft.loadingChannels = true
}

const getExternalChannelsFailure = (draft: Draft<IExternalChannelState>) => {
  draft.loadingChannels = false
}

function isIGetExternalChannelsSuccessAction(arg: any): arg is IAction<IGetExternalChannelsSuccessResponseBody> {
  return arg.type === ExternalChannelActionTypes.GET_EXTERNAL_CHANNELS_SUCCESS
}

const getExternalChannelsSuccess = (draft: Draft<IExternalChannelState>, action: TAction) => {
  if (!isIGetExternalChannelsSuccessAction(action)) { return }

  draft.externalChannels = action.payload.externalChannels
  draft.loadingChannels = false
}

const openActionsDropdown = (draft: Draft<IExternalChannelState>) => {
  draft.page.actionsDropdownOpen = true
}

const closeDropdown = (draft: Draft<IExternalChannelState>) => {
  draft.page.actionsDropdownOpen = false
}
